import { computed, type ComputedRef } from 'vue'
import store from '@/js/store'
import { useStudiengangStore } from '@/js/stores'

/**
 * Frontend-Spiegelung von App\Application\Security\KennzahlenAccessResolver.
 *
 * Lesezugriff auf Kennzahlen/Kennwerte:
 * - seesAllKennwerte: Admin, Hochschulleitung, QMB mit isForAll
 * - sonst eingeschränkt auf allowedStudiengangIds (SFB / QMB mit Liste) bzw.
 *   allowedFachbereichIds (Dekan / Studiendekan)
 * - canViewKennwerte=false → Lehrender, Modulverantwortlicher, Gast, kein Login
 *
 * Backend filtert API-seitig zusätzlich — diese Werte dienen rein der UX
 * (Routen-Guard, Dropdown-Reduktion).
 */
interface RolleFrontend {
    rollenName?: string
    isAdmin?: boolean
    isForAll?: boolean
    studiengangIds?: string[]
    fachbereichIds?: string[]
    modulIds?: string[]
}

const ROLE_NAMES = {
    Admin: 'Admin',
    Hochschulleitung: 'Hochschulleitung',
    Dekan: 'Dekan',
    QMB: 'Qualitätsmanagement Beauftragte(r)',
    Studiendekan: 'Studiendekan',
    Studienfachberater: 'Studienfachberater(in)'
} as const

export interface KennwerteAccess {
    canViewKennwerte: ComputedRef<boolean>
    seesAllKennwerte: ComputedRef<boolean>
    allowedStudiengangIds: ComputedRef<string[]>
    allowedFachbereichIds: ComputedRef<string[]>
}

export function useKennwerteAccess(): KennwerteAccess {
    const studiengangStore = useStudiengangStore()

    const rollen = computed<Record<string, RolleFrontend>>(() => {
        const user = store.getters['security/currentUser']
        return user && user.rollen ? user.rollen : {}
    })

    const seesAllKennwerte = computed<boolean>(() => {
        const map = rollen.value
        if (map[ROLE_NAMES.Admin] || map[ROLE_NAMES.Hochschulleitung]) {
            return true
        }
        const qmb = map[ROLE_NAMES.QMB]
        if (qmb && qmb.isForAll) {
            return true
        }
        const dekan = map[ROLE_NAMES.Dekan]
        if (dekan && dekan.isForAll) {
            return true
        }
        const sd = map[ROLE_NAMES.Studiendekan]
        if (sd && sd.isForAll) {
            return true
        }
        const sfb = map[ROLE_NAMES.Studienfachberater]
        if (sfb && sfb.isForAll) {
            return true
        }
        return false
    })

    const allowedStudiengangIds = computed<string[]>(() => {
        if (seesAllKennwerte.value) {
            return []
        }
        const ids = new Set<string>()

        const dekan = rollen.value[ROLE_NAMES.Dekan]
        if (dekan) {
            studiengangStore.studiengaenge
                .filter((stg) => {
                    const iri = stg.fachbereich
                    const id = iri?.replace(/\/$/, '').split('/').pop()
                    return dekan.fachbereichIds?.includes(id)
                })
                .forEach((stg) => ids.add(stg.studiengangId))
        }

        const sd = rollen.value[ROLE_NAMES.Studiendekan]
        if (sd) {
            studiengangStore.studiengaenge
                .filter((stg) => {
                    const iri = stg.fachbereich
                    const id = iri?.replace(/\/$/, '').split('/').pop()
                    return sd.fachbereichIds?.includes(id)
                })
                .forEach((stg) => ids.add(stg.studiengangId))
        }

        const sfb = rollen.value[ROLE_NAMES.Studienfachberater]
        sfb?.studiengangIds?.forEach((id) => ids.add(id))
        const qmb = rollen.value[ROLE_NAMES.QMB]
        if (qmb && !qmb.isForAll) {
            qmb.studiengangIds?.forEach((id) => ids.add(id))
        }
        return Array.from(ids)
    })

    const allowedFachbereichIds = computed<string[]>(() => {
        if (seesAllKennwerte.value) {
            return []
        }
        const ids = new Set<string>()
        const sd = rollen.value[ROLE_NAMES.Studiendekan]
        sd?.fachbereichIds?.forEach((id) => ids.add(id))
        const dekan = rollen.value[ROLE_NAMES.Dekan]
        dekan?.fachbereichIds?.forEach((id) => ids.add(id))
        return Array.from(ids)
    })

    const canViewKennwerte = computed<boolean>(() => {
        if (seesAllKennwerte.value) {
            return true
        }
        return allowedStudiengangIds.value.length > 0 || allowedFachbereichIds.value.length > 0
    })

    return {
        canViewKennwerte,
        seesAllKennwerte,
        allowedStudiengangIds,
        allowedFachbereichIds
    }
}
